import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, SlidersHorizontal } from 'lucide-react';
import { ADJUSTMENT_REASONS, ADJUSTMENT_REASON_KEYS, formatMoney, type AdjustmentReason, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Button, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';
import { Tabs } from '../../ui/inputs';
import { LinesEditor, linesReady, type DocLine } from './LinesEditor';

interface StockDoc {
  id: string;
  number: string;
  kind: string;
  reason: string | null;
  note: string | null;
  valueCents: number;
  createdAt: string;
  branchName: string;
  toBranchName: string | null;
  createdByName: string | null;
}

export function StockPage() {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [tab, setTab] = useState<'adjustments' | 'transfers'>('adjustments');
  const [adjusting, setAdjusting] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const docs = useQuery({ queryKey: ['stock-docs', tab], queryFn: () => api.get<StockDoc[]>(`/api/stock/${tab}`) });
  const valuation = useQuery({
    queryKey: ['valuation'],
    queryFn: () => api.get<{ branchId: string; branchName: string; valueCents: number; skuCount: number; negativeCount: number }[]>('/api/stock/valuation'),
    enabled: can('products.view_cost') || can('reports.stock'),
  });

  return (
    <>
      <PageHeader
        title="Stock control"
        description="Adjustments and transfers between branches. Every change is recorded with who made it and why."
        actions={
          <>
            {can('stock.transfer') && me.branches.length > 0 && (
              <Button icon={<ArrowLeftRight className="size-4" />} onClick={() => setTransferring(true)}>Transfer stock</Button>
            )}
            {can('stock.adjust') && <Button variant="primary" icon={<SlidersHorizontal className="size-4" />} onClick={() => setAdjusting(true)}>Adjust stock</Button>}
          </>
        }
      />
      {valuation.data && valuation.data.length > 0 && (
        <div className="mb-6 grid gap-px overflow-hidden rounded-[var(--radius-panel)] border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {valuation.data.map((v) => (
            <div key={v.branchId} className="bg-surface p-4">
              <p className="text-sm text-muted">Stock value at {v.branchName}</p>
              <p className="mt-1 text-2xl font-semibold num">{formatMoney(v.valueCents, cur)}</p>
              <p className="text-sm text-muted">
                {v.skuCount} products in stock{v.negativeCount ? `, ${v.negativeCount} below zero` : ''}
              </p>
            </div>
          ))}
        </div>
      )}
      <Tabs value={tab} onChange={setTab} tabs={[{ value: 'adjustments', label: 'Adjustments' }, { value: 'transfers', label: 'Transfers' }]} />
      <Panel>
        {docs.isLoading && <Spinner />}
        {docs.error && <div className="p-4"><ErrorNotice error={docs.error} /></div>}
        {docs.data?.length === 0 && <EmptyState title={tab === 'adjustments' ? 'No adjustments yet' : 'No transfers yet'} />}
        {!!docs.data?.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Number</th>
                  <th className="px-3 py-2.5 font-medium">{tab === 'adjustments' ? 'Reason' : 'Route'}</th>
                  <th className="px-3 py-2.5 font-medium">By</th>
                  {can('products.view_cost') && <th className="px-5 py-2.5 text-right font-medium">Value</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {docs.data.map((d) => (
                  <tr key={d.id}>
                    <td className="px-5 py-3">
                      <span className="font-medium">{d.number}</span>
                      <span className="block text-xs text-muted">{formatDateTime(d.createdAt)}</span>
                    </td>
                    <td className="px-3 py-3">
                      {tab === 'adjustments' ? `${ADJUSTMENT_REASONS[d.reason as AdjustmentReason] ?? d.reason} at ${d.branchName}` : `${d.branchName} → ${d.toBranchName}`}
                      {d.note && <span className="block text-sm text-muted">{d.note}</span>}
                    </td>
                    <td className="px-3 py-3 text-sm">{d.createdByName}</td>
                    {can('products.view_cost') && <td className="px-5 py-3 text-right num">{formatMoney(d.valueCents, cur)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {adjusting && <AdjustDialog onClose={() => setAdjusting(false)} />}
      {transferring && <TransferDialog onClose={() => setTransferring(false)} />}
    </>
  );
}

function AdjustDialog({ onClose }: { onClose: () => void }) {
  const { me, can } = useSession();
  const { branch } = useCurrentBranch();
  const qc = useQueryClient();
  const toast = useToast();
  const [branchId, setBranchId] = useState(branch?.id ?? '');
  const [reason, setReason] = useState<AdjustmentReason>('damaged');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DocLine[]>([]);
  const opening = reason === 'opening_stock';
  const m = useMutation({
    mutationFn: () =>
      api.post<{ number: string }>('/api/stock/adjustments', {
        branchId,
        reason,
        note,
        lines: lines.map((l) => {
          const dir = opening ? 'in' : (l.direction ?? 'out');
          return { productId: l.product.id, qtyChangeMilli: dir === 'in' ? l.qtyMilli : -(l.qtyMilli ?? 0), unitCostCents: dir === 'in' ? l.unitCostCents : null };
        }),
      }),
    onSuccess: (d) => {
      toast(`Adjustment ${d.number} saved`);
      for (const k of ['stock-docs', 'products', 'valuation']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title="Adjust stock"
      description="For breakages, losses, stock found and opening balances. Use a stocktake for full counts."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!linesReady(lines)} busy={m.isPending} onClick={() => m.mutate()}>Save adjustment</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Field label="Branch" htmlFor="adj-branch">
          <Select id="adj-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Reason" htmlFor="adj-reason" error={f.reason}>
          <Select id="adj-reason" value={reason} onChange={(e) => setReason(e.target.value as AdjustmentReason)}>
            {ADJUSTMENT_REASON_KEYS.map((k) => <option key={k} value={k}>{ADJUSTMENT_REASONS[k]}</option>)}
          </Select>
        </Field>
        <Field label="Note" htmlFor="adj-note">
          <TextInput id="adj-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened?" />
        </Field>
      </div>
      <LinesEditor lines={lines} onChange={setLines} branchId={branchId} showDirection={!opening} showCost={can('products.view_cost')} />
    </Dialog>
  );
}

function TransferDialog({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const qc = useQueryClient();
  const toast = useToast();
  const allBranches = useQuery({ queryKey: ['branches'], queryFn: () => api.get<{ id: string; name: string; isActive: boolean }[]>('/api/branches') });
  const [from, setFrom] = useState(branch?.id ?? '');
  const destinations = (allBranches.data ?? me.branches.map((b) => ({ ...b, isActive: true }))).filter((b) => b.isActive && b.id !== from);
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DocLine[]>([]);
  const m = useMutation({
    mutationFn: () =>
      api.post<{ number: string }>('/api/stock/transfers', {
        fromBranchId: from,
        toBranchId: to,
        note,
        lines: lines.map((l) => ({ productId: l.product.id, qtyMilli: l.qtyMilli })),
      }),
    onSuccess: (d) => {
      toast(`Transfer ${d.number} done`);
      for (const k of ['stock-docs', 'products', 'valuation']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title="Transfer stock"
      description="Moves stock from one branch to another at its average cost."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!to || !linesReady(lines)} busy={m.isPending} onClick={() => m.mutate()}>Transfer</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Field label="From" htmlFor="tr-from">
          <Select id="tr-from" value={from} onChange={(e) => setFrom(e.target.value)}>
            {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="To" htmlFor="tr-to">
          <Select id="tr-to" value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">Choose…</option>
            {destinations.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Note" htmlFor="tr-note">
          <TextInput id="tr-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Driver and vehicle" />
        </Field>
      </div>
      <LinesEditor lines={lines} onChange={setLines} branchId={from} />
    </Dialog>
  );
}
